import { keepPreviousData } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useGetApiV1NotificationMobileAppSharedClassStudents } from '@/shared/api/generated/hooks/notificationMobileAppController/useGetApiV1NotificationMobileAppSharedClassStudents';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';
import type { NotificationClassroom } from '../types/notification';

type ClassroomPagedResponse = {
  currentPage?: number;
  totalPages?: number;
  totalCount?: number;
  data?: Array<{ id?: number; code?: string | null; name?: string | null; totalEnrolled?: number }>;
};

function unpackClassroomResponse(raw: unknown): {
  classrooms: NotificationClassroom[];
  totalPages: number;
  totalCount: number;
} {
  const items = unwrapPagedResponse<{
    id?: number;
    code?: string | null;
    name?: string | null;
    totalEnrolled?: number;
  }>(raw);
  const paged = raw as ClassroomPagedResponse | undefined;

  const classrooms = items.map(item => ({
    id: String(item.id ?? ''),
    name: [item.code, item.name].filter(Boolean).join(' / '),
    institution: '',
    studentCount: item.totalEnrolled ?? 0,
  }));

  return {
    classrooms,
    totalPages: paged?.totalPages ?? 1,
    totalCount: paged?.totalCount ?? classrooms.length,
  };
}

/**
 * Hook para buscar turmas com paginação e filtro server-side.
 * Gerencia internamente `pageIndex` (0-based), `search` (com debounce de 400ms) e `pageSize`.
 *
 * A paginação usa `totalPages` retornado diretamente pela API.
 *
 * @param pageSize - Número de itens por página
 * @returns Dados paginados, estado de loading e handlers de controle
 *
 * @example
 * ```tsx
 * const { classrooms, pageIndex, setPageIndex, search, setSearch } = useClassroomList(8);
 * ```
 */
export function useClassroomList(pageSize: number) {
  // pageIndex é 0-based (para o componente Pagination do ADS)
  // pageNumber é 1-based (para a API)
  const [pageIndex, setPageIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPageIndex(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: rawData, isFetching } = useGetApiV1NotificationMobileAppSharedClassStudents(
    {
      ...(debouncedSearch ? { searchText: debouncedSearch } : {}),
      pageNumber: pageIndex + 1,
      pageSize,
    },
    { query: { placeholderData: keepPreviousData } },
  );

  const { classrooms, totalPages } = unpackClassroomResponse(rawData);

  return {
    classrooms,
    pageIndex,
    setPageIndex,
    totalPages,
    search,
    setSearch,
    isLoading: isFetching,
  } as const;
}
